import { GraphApiService } from '@nuso/common/data-access-api';
import { Injectable, inject } from '@angular/core';
import {
  createIamAppUserMutation,
  updateIamAppUserMutation,
  deleteIamAppUserMutation,
  attachIamPortalUserMutation,
  detachIamPortalUserMutation,
  attachIamMediaPointToAppUserMutation,
  detachIamMediaPointFromAppUserMutation,
  attachIamAppUserToMediaPointMutation,
  detachIamAppUserFromMediaPointMutation,
  getIamAppUsersAvailableForGroupQuery,
  getIamPortalUsersAvailableForAppUserQuery,
  getIamMediaPointsAvailableForAppUserQuery,
  getIamAppUsersAvailableForMediaPointQuery,
  searchIamAppUsersQuery,
  getIamAppUserQuery,
  lookupIamAppUsersByNameQuery,
} from './iam-app-user.queries';
import { AppUser, CreateApUserInput, UpdateApUserInput } from '@nuso/iam/types';

@Injectable({
  providedIn: 'root',
})
export class IamAppUserService {
  private graphApi = inject(GraphApiService);


  searchAppUsers() {
    return this.graphApi.watchQuery<{ totalCount: number, appUsers: AppUser[] }>({
      fetchPolicy: 'network-only',
      query: searchIamAppUsersQuery,
      variables: {
        input: {},
      },
    });
  }

  getIamAppUser(appUserId: string) {
    return this.graphApi.watchQuery<AppUser>({
      query: getIamAppUserQuery,
      variables: {
        input: { id: appUserId },
      },
    });
  }

  lookupIamAppUsersByName(name: string) {
    return this.graphApi.watchQuery<[AppUser]>({
      query: lookupIamAppUsersByNameQuery,
      variables: {
        name,
      },
    });
  }

  getPortalUsersAvailableForAppUser() {
    return this.graphApi.watchQuery<any>({
      fetchPolicy: 'network-only',
      query: getIamPortalUsersAvailableForAppUserQuery,
      variables: {
      },
    });
  }

  getMediaPointsAvailableForAppUser(id: string) {
    return this.graphApi.watchQuery<any>({
      fetchPolicy: 'network-only',
      query: getIamMediaPointsAvailableForAppUserQuery,
      variables: {
        id
      },
    });
  }

  getAppUsersAvailableForGroup() {
    return this.graphApi.watchQuery<any>({
      fetchPolicy: 'network-only',
      query: getIamAppUsersAvailableForGroupQuery,
      variables: {
        input: {
        },
      },
    });
  }

  getAppUsersAvailableForMediaPoint(id: string) {
    return this.graphApi.watchQuery<any>({
      fetchPolicy: 'network-only',
      query: getIamAppUsersAvailableForMediaPointQuery,
      variables: {
        id
      },
    });
  }

  createAppUser(input: CreateApUserInput) {
    return this.graphApi.mutate({
      mutation: createIamAppUserMutation,
      variables: {
        input,
      },
    });
  }

  updateAppUser(input: UpdateApUserInput) {
    return this.graphApi.mutate({
      mutation: updateIamAppUserMutation,
      variables: {
        input,
      },
    });
  }

  deleteAppUser(id: string) {
    return this.graphApi.mutate({
      mutation: deleteIamAppUserMutation,
      variables: {
        input: {
          id
        },
      },
    });
  }

  attachPortalUser(input: { appUserId: string, portalUserId: number }) {
    return this.graphApi.mutate({
      mutation: attachIamPortalUserMutation,
      variables: {
        input
      },
    });
  }

  detachPortalUser(appUserId: string) {
    return this.graphApi.mutate({
      mutation: detachIamPortalUserMutation,
      variables: {
        input: {
          id: appUserId
        }
      },
    });
  }

  attachMediaPointToAppUser(appUserId: string, mediaPointId: string) {
    return this.graphApi.mutate({
      mutation: attachIamMediaPointToAppUserMutation,
      variables: {
        input: {
          appUserId,
          mediaPointId,
        }
      },
    });
  }

  detachMediaPointFromAppUser(appUserId: string, mediaPointId: string) {
    return this.graphApi.mutate({
      mutation: detachIamMediaPointFromAppUserMutation,
      variables: {
        input: {
          appUserId,
          mediaPointId,
        }
      },
    });
  }

  attachAppUserToMediaPoint(appUserId: string, mediaPointId: string) {
    return this.graphApi.mutate({
      mutation: attachIamAppUserToMediaPointMutation,
      variables: {
        input: {
          appUserId,
          mediaPointId,
        }
      },
    });
  }

  detachAppUserFromMediaPoint(appUserId: string, mediaPointId: string) {
    return this.graphApi.mutate({
      mutation: detachIamAppUserFromMediaPointMutation,
      variables: {
        input: {
          appUserId,
          mediaPointId,
        }
      },
    });
  }
}
